/**
 * Full notice detail — opened from the Notices-sent log or a case's
 * Notifications tab. Shows the email exactly as it went out (stored at send
 * time, banner/footer included), per-recipient delivery status, and the raw
 * event timeline.
 */

import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { api } from '../lib/api';

interface Recipient {
  id: number;
  email: string;
  role: 'to' | 'cc' | 'bcc' | 'test';
  status: string;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  send_error: string | null;
}

interface NoticeDetail {
  notification: {
    id: number;
    case_id: number;
    party: string | null;
    subject: string | null;
    body_html: string | null;
    sent_subject: string | null;
    sent_body_html: string | null;
    status: string;
    test_mode: 0 | 1;
    to_emails: string[];
    cc_emails: string[];
    sent_at: string | null;
    send_error: string | null;
    created_by_email?: string | null;
  };
  recipients: Recipient[];
  events: { id: number; event_type: string; source: string; created_at: string; recipient?: string | null; detail?: string | null }[];
  case_context: { franchisee_af: string | null; owner_af: string | null; case_state: string | null } | null;
}

const STATUS_DOT: Record<string, string> = {
  pending: 'warn', sent: 'ok', delivered: 'ok', opened: 'ok', bounced: 'danger', failed: 'danger',
  draft: 'warn', queued: 'warn',
};

const ROLE_LABEL: Record<string, string> = { to: 'To', cc: 'CC', bcc: 'BCC', test: 'Test' };

export function NoticeDetailModal({ noticeId, onClose, onChanged }: { noticeId: number; onClose: () => void; onChanged?: () => void }) {
  const [detail, setDetail] = useState<NoticeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [from, setFrom] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  useEffect(() => {
    api<{ from?: string }>('/api/notifications/mode').then((m) => setFrom(m.from ?? null)).catch(() => {});
  }, []);

  const load = () => {
    api<NoticeDetail>(`/api/notifications/${noticeId}`)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  };
  useEffect(load, [noticeId]);

  const checkStatus = async () => {
    setBusy(true);
    try {
      await api(`/api/notifications/${noticeId}/refresh`, { method: 'POST' });
      load();
    } catch { /* non-fatal */ } finally { setBusy(false); }
  };

  const n = detail?.notification;
  const wasSent = !!n?.sent_at;
  // Retry: a notice nobody received. Send to missed: a sent notice some
  // recipients didn't get (e.g. the 2026-11-02 rate-limit refusals).
  const canRetry = !!n && n.status === 'failed' && !wasSent;
  const missed = n && wasSent && !n.test_mode ? (detail?.recipients ?? []).filter((r) => r.status === 'failed' && r.role !== 'test') : [];

  const act = async (path: string, question: string, done: string) => {
    if (!window.confirm(question)) return;
    setBusy(true); setActionErr(null); setActionMsg(null);
    try {
      await api(`/api/notifications/${noticeId}/${path}`, { method: 'POST', body: {} });
      setActionMsg(done);
      load();
      onChanged?.();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : 'Send failed');
      load();
    } finally { setBusy(false); }
  };
  const retry = () => n && act('send',
    `Send notice #${noticeId} now?\n\nTo: ${n.to_emails.join(', ') || '(none)'}${n.cc_emails.length ? `\nCC: ${n.cc_emails.join(', ')}` : ''}${n.test_mode ? '' : '\n\nThis is a live, franchisee-facing send.'}`,
    'Sent.');
  const sendMissed = () => act('send-missed',
    `Send notice #${noticeId}, exactly as sent, to the ${missed.length} recipient${missed.length === 1 ? '' : 's'} who didn't get it?\n\n${missed.map((r) => `${(ROLE_LABEL[r.role] ?? r.role).toUpperCase()}: ${r.email}`).join('\n')}`,
    'Sent to the missed recipients.');

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1200 }} />
      <div style={{
        position: 'fixed', top: '4vh', left: '50%', transform: 'translateX(-50%)',
        width: 'min(860px, 94vw)', maxHeight: '92vh', overflowY: 'auto',
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
        zIndex: 1201, boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
          <Icon name="bell" size={15} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              Notice #{noticeId}
              {n?.party && <span className="muted" style={{ fontWeight: 400 }}> · {n.party === 'offending' ? 'Servicing franchisee' : 'Territory owner'}</span>}
            </div>
            {detail?.case_context && (
              <div className="muted" style={{ fontSize: 12 }}>
                <span className="mono">CASE-{n?.case_id}</span> · {detail.case_context.franchisee_af}
                {detail.case_context.owner_af && detail.case_context.owner_af !== detail.case_context.franchisee_af ? ` → ${detail.case_context.owner_af}` : ''}
                {' '}· case {detail.case_context.case_state}
              </div>
            )}
          </div>
          {n && (
            <span className="pill" style={{ height: 22, fontSize: 11 }}>
              <span className={`pill-dot dot-${STATUS_DOT[n.status] ?? 'warn'}`}></span>
              {n.status.toUpperCase()}{wasSent && n.test_mode ? ' (TEST)' : ''}
            </span>
          )}
          {canRetry && (
            <button className="btn btn-sm btn-danger" onClick={retry} disabled={busy} title="Send this notice again — nobody received it">
              <Icon name="refresh" size={12} /> Retry send
            </button>
          )}
          {missed.length > 0 && (
            <button className="btn btn-sm btn-danger" onClick={sendMissed} disabled={busy} title="Send this notice, exactly as sent, to only the recipients who didn't get it">
              <Icon name="refresh" size={12} /> Send to missed ({missed.length})
            </button>
          )}
          {wasSent && (
            <button className="btn btn-sm btn-ghost" onClick={checkStatus} disabled={busy} title="Poll Resend for latest delivery status">
              <Icon name="refresh" size={12} /> {busy ? 'Checking…' : 'Check status'}
            </button>
          )}
          <a className="btn btn-sm btn-ghost" href={`/?case=${n?.case_id ?? ''}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <Icon name="ext" size={12} /> Open case
          </a>
          <button className="btn btn-sm btn-ghost btn-icon" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        {error && <div className="c-danger" style={{ padding: 16, fontSize: 13 }}>{error}</div>}
        {!detail && !error && <div className="muted" style={{ padding: 16, fontSize: 13 }}>Loading…</div>}

        {detail && n && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {actionMsg && <div className="c-ok" style={{ fontSize: 12.5 }}>{actionMsg}</div>}
            {actionErr && <div className="c-danger" style={{ fontSize: 12.5 }}>{actionErr}</div>}
            {n.send_error && (
              <div className="c-danger" style={{ fontSize: 12.5 }}>Send errors: {n.send_error}</div>
            )}

            {/* Per-recipient delivery status */}
            <div>
              <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                Recipients ({detail.recipients.length})
              </div>
              {detail.recipients.length === 0 && (
                <div className="muted" style={{ fontSize: 12.5 }}>Not sent yet — recipients get individual tracking once the notice is sent.</div>
              )}
              {detail.recipients.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left', background: 'var(--surface-2)' }}>
                        {['Recipient', '', 'Status', 'Sent', 'Delivered', 'Opened'].map((h, i) => (
                          <th key={i} className="muted" style={{ padding: '8px 12px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.recipients.map((r) => (
                        <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td className="mono" style={{ padding: '8px 12px' }}>{r.email}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span className="faint" style={{ fontSize: 10.5, textTransform: 'uppercase' }}>{ROLE_LABEL[r.role]}</span>
                          </td>
                          <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                            <span className="pill" style={{ height: 20, fontSize: 10.5 }}>
                              <span className={`pill-dot dot-${STATUS_DOT[r.status] ?? 'warn'}`}></span>
                              {r.status.toUpperCase()}
                            </span>
                            {r.send_error && <div className="c-danger" style={{ fontSize: 11, marginTop: 2 }}>{r.send_error}</div>}
                          </td>
                          <td className="muted" style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmt(r.sent_at)}</td>
                          <td className="muted" style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmt(r.delivered_at)}</td>
                          <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                            {r.opened_at ? <span className="c-ok">{fmt(r.opened_at)}</span> : <span className="faint">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* The email as actually sent */}
            <div>
              <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                {wasSent ? 'Email as sent' : 'Draft email'}
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 12.5 }}>
                  <div><span className="faint">From:</span> {from ?? 'notices@amadalabs.com'}</div>
                  <div style={{ marginTop: 2 }}><span className="faint">Subject:</span> <b>{n.sent_subject ?? n.subject}</b></div>
                </div>
                <div
                  style={{ padding: '14px 18px', fontSize: 13, lineHeight: 1.55, background: '#fff', color: '#1a1a1a' }}
                  dangerouslySetInnerHTML={{ __html: n.sent_body_html ?? n.body_html ?? '<em>(empty)</em>' }}
                />
              </div>
            </div>

            {/* Event timeline */}
            {detail.events.length > 0 && (
              <div>
                <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  Delivery events
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {detail.events.map((e) => (
                    <div key={e.id} style={{ display: 'flex', gap: 10, fontSize: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span className="muted mono" style={{ whiteSpace: 'nowrap' }}>{fmt(e.created_at)}</span>
                      <span className={`mono${/bounced|complained|failed/.test(e.event_type) ? ' c-danger' : ''}`}>{e.event_type}</span>
                      {e.recipient && <span className="mono">{e.recipient}</span>}
                      <span className="faint" style={{ fontSize: 10.5 }}>via {e.source}</span>
                      {e.detail && <div className="c-danger" style={{ flexBasis: '100%', fontSize: 11.5, paddingLeft: 4 }}>{e.detail}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {n.created_by_email && (
              <div className="faint" style={{ fontSize: 11.5 }}>Sent by {n.created_by_email}{n.sent_at ? ` · ${fmt(n.sent_at)}` : ''}</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function fmt(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
